import React from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import { DashboardSectionTitle } from './DashboardSectionTitle';
import { formatDisplayDate } from '../../utils/dashboardData';
import { formatKilos, formatPercent } from '../../utils/flujoCalculations';

export const DailyEvolutionCard = ({ rows, showSegments = false }) => {
  const maxKilos = Math.max(...rows.map((row) => row.kilosVolcados), 1);
  const legendItems = showSegments
    ? rows.find((row) => row.segments?.length)?.segments || []
    : [];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        height: '100%',
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          mb: 2,
        }}
      >
        <DashboardSectionTitle icon={<TimelineRoundedIcon />}>
          Evolucion por dias
        </DashboardSectionTitle>

        {legendItems.length > 0 && (
          <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', justifyContent: 'flex-end', gap: 1 }}>
            {legendItems.map((segment) => (
              <Stack key={segment.label} direction="row" spacing={0.6} sx={{ alignItems: 'center' }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: segment.color,
                    flex: '0 0 auto',
                  }}
                />
                <Typography sx={{ color: 'text.secondary', fontSize: 12, fontWeight: 700 }}>
                  {segment.label}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Stack>
      <Stack spacing={1.6}>
        {rows.map((row) => {
          const percent = (row.kilosVolcados * 100) / maxKilos;

          return (
            <Box key={row.fecha}>
              <Stack
                direction="row"
                spacing={1.5}
                sx={{ justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 900 }}>
                    {formatDisplayDate(row.fecha)}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                    {row.registros} registros
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right', flex: '0 0 auto' }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 900 }}>
                    {formatKilos(row.kilosVolcados)} kg - {formatKilos(row.kilosClasificados)} kg clasificados - {formatPercent(row.rendimiento)} %
                  </Typography>
                  {showSegments ? (
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ flexWrap: 'wrap', justifyContent: 'flex-end', gap: 0.75 }}
                    >
                      {row.segments.map((segment) => (
                        <Stack
                          key={segment.label}
                          direction="row"
                          spacing={0.5}
                          sx={{ alignItems: 'center' }}
                        >
                          <Box
                            sx={{
                              width: 7,
                              height: 7,
                              borderRadius: '50%',
                              bgcolor: segment.color,
                              flex: '0 0 auto',
                            }}
                          />
                          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                            {formatKilos(segment.value)} kg
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  ) : (
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                      {formatKilos(row.kilosClasificados)} kg clasificados - {formatPercent(row.rendimiento)} %
                    </Typography>
                  )}
                </Box>
              </Stack>
              <Box
                sx={{
                  height: 8,
                  borderRadius: 1,
                  bgcolor: '#e5e7eb',
                  display: 'flex',
                  overflow: 'hidden',
                }}
              >
                {showSegments
                  ? row.segments.map((segment) => (
                    <Box
                      key={segment.label}
                      sx={{
                        width: `${maxKilos > 0 ? (segment.value * 100) / maxKilos : 0}%`,
                        bgcolor: segment.color,
                      }}
                    />
                  ))
                  : (
                    <Box
                      sx={{
                        width: `${Math.min(percent, 100)}%`,
                        bgcolor: 'primary.main',
                      }}
                    />
                  )}
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
};
